import type { GatheringEntranceSpot } from '@sorrem-client/game/render-config';
import { Callout } from '@sorrem-client/ui/components';
import { resolveCrucibleDescription } from '@sorrem-client/ui/features/CharacterHome/hooks';
import { HudAreaFrame, HudAreaSlot, PartyPanel, PartyTabs, usePartyChatTab, type PartyPanelMarkerToggle, type PartyPanelRow, type PartyPanelRowAction } from '@sorrem-client/ui/features/Hud';
import { GuidedTour } from '@sorrem-client/ui/features/GuidedTour';
import { LoadingPage } from '@sorrem-client/ui/features/LoadingPage';
import { useCallout } from '@sorrem-client/ui/hooks';
import { DEFAULT_MIN_PLAYERS, isPartyCrucible, resolveVeilSaturationById, type CrucibleMember, type CrucibleRecord, type StartingCrystalPick } from '@sorrem/shared';
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { resolveGatheringPartyRows } from '../gathering-party-rows';
import { buildInviteLink } from '../use-invite-link';
import { CruciblePanel } from './CruciblePanel';
import { GatheringInventory } from './GatheringInventory';
import styles from './GatheringScreen.module.scss';
import { TitleOverlay } from './TitleOverlay';

/** The countdown's box, as a share of how tall the entrance's hole is drawn: its digits' height (gathering-set.ts). */
const COUNTDOWN_ANCHOR_SHARE = 0.73;

interface GatheringScreenProps {
    readonly crucible: CrucibleRecord | null;
    readonly members: readonly CrucibleMember[];
    /** The viewer's own party member, or null while they aren't in the party. */
    readonly self: CrucibleMember | null;
    /** Whether the gathering set is built, so it shows instead of the loading view. */
    readonly isGatheringReady: boolean;
    /** How far the gathering is from showing, 0 to 1, while it isn't. */
    readonly gatheringLoadProgress: number;
    /** The party connection's status, over the gathering or under the loading bar. */
    readonly partyStatus: ReactNode;
    /** The run's before-cutscene, over everything, while it plays. */
    readonly beforeCutscene: ReactNode;
    /** Ready (or the run starting) locks gear and crystals. */
    readonly isLoadoutLocked: boolean;
    readonly stageStartingCrystals: (picks: StartingCrystalPick[]) => Promise<string | null>;
    readonly isStarting: boolean;
    readonly isDirectEntry: boolean;
    readonly countdownSecondsRemaining: number | null;
    /** The countdown over the entrance: as `countdownSecondsRemaining`, held at 0 while the run starts. */
    readonly entryCountdownSeconds: number | null;
    /** Where that countdown stands over the entrance and how big the entrance is drawn; null until the gathering is built. */
    readonly entrance: GatheringEntranceSpot | null;
    readonly isRunLoaded: boolean;
    readonly readyError: string | null;
    readonly partyRowActions: (row: PartyPanelRow) => readonly PartyPanelRowAction[];
    readonly partyMarkerToggle: (row: PartyPanelRow) => PartyPanelMarkerToggle | null;
    /** The viewer can ready up and hasn't: the Party tab flashes while Chat shows. */
    readonly isAwaitingReady: boolean;
}

/**
 * The party gathering before a run: the crucible's panel, the entering
 * character's loadout, and the party panel with its chat, in the run's HUD
 * areas so each panel stands where its counterpart will in the run. The
 * entry countdown is drawn by the scene over its entrance (gathering-set.ts);
 * the first time it runs, a callout beside it says what it is.
 */
export function GatheringScreen({
    crucible,
    members,
    self,
    isGatheringReady,
    gatheringLoadProgress,
    partyStatus,
    beforeCutscene,
    isLoadoutLocked,
    stageStartingCrystals,
    isStarting,
    isDirectEntry,
    countdownSecondsRemaining,
    entryCountdownSeconds,
    entrance,
    isRunLoaded,
    readyError,
    partyRowActions,
    partyMarkerToggle,
    isAwaitingReady,
}: GatheringScreenProps): JSX.Element {
    const partyChat = usePartyChatTab();
    const [isCruciblePanelOpen, setIsCruciblePanelOpen] = useState(true);
    const countdownRef = useRef<HTMLDivElement>(null);
    // Explains the countdown to entry once everyone is ready; the first entry it explained retires it.
    const { isOpen: isCountdownCalloutShown, close: closeCallout, dismiss: dismissCallout } = useCallout('entry-countdown');
    const isCountingDown = isGatheringReady && !isDirectEntry && !isStarting && entryCountdownSeconds !== null && entryCountdownSeconds > 0;
    const isCalloutOpen = isCountdownCalloutShown && isCountingDown && entrance !== null;
    const wasCalloutShownRef = useRef(false);
    if (isCalloutOpen) {
        wasCalloutShownRef.current = true;
    }

    // Entering the crucible after the callout explained the countdown retires it.
    useEffect(() => {
        if (isStarting && wasCalloutShownRef.current) {
            dismissCallout();
        }
    }, [dismissCallout, isStarting]);
    const leaderAccountId = crucible?.createdBy ?? null;
    const partyRows = useMemo(
        () => (leaderAccountId === null ? [] : resolveGatheringPartyRows(members, leaderAccountId)),
        [leaderAccountId, members],
    );

    return (
        <div className={styles.screen}>
            {crucible && (
                <TitleOverlay
                    title={crucible.displayName}
                    description={resolveCrucibleDescription(crucible.backstory ?? null, crucible.veilSaturation)}
                />
            )}

            {isGatheringReady
                ? <div className={styles.statusOverlay}>{partyStatus}</div>
                : (
                    <LoadingPage progress={gatheringLoadProgress}>
                        {partyStatus}
                    </LoadingPage>
                )}

            {/* Where the scene draws the countdown's number, for the callout to point at. */}
            {isCountingDown && entrance !== null && (
                <div ref={countdownRef} className={styles.countdownAnchor} style={resolveCountdownAnchorStyle(entrance)} aria-hidden='true' />
            )}
            <Callout open={isCalloutOpen} anchorRef={countdownRef} placement='right' onClose={closeCallout} onDismiss={dismissCallout}>
                Everyone is ready. When this countdown ends, the party enters the Crucible. Click Ready beside your name to stop it.
            </Callout>

            {/* The run's HUD areas, so each panel stands where its counterpart will in the run. */}
            <HudAreaFrame>
                {crucible && (
                    <>
                        <GatheringInventory
                            members={members}
                            isLocked={isLoadoutLocked}
                            stageStartingCrystals={stageStartingCrystals}
                        />
                        <HudAreaSlot panel="commission">
                            <CruciblePanel
                                open={isCruciblePanelOpen}
                                onOpenChange={setIsCruciblePanelOpen}
                                edge="right"
                                saturation={resolveVeilSaturationById(crucible.veilSaturation)}
                                members={members}
                                minPlayers={crucible.minPlayers ?? DEFAULT_MIN_PLAYERS}
                                self={self}
                                isStarting={isStarting}
                                isDirectEntry={isDirectEntry}
                                countdownSecondsRemaining={countdownSecondsRemaining}
                                isRunLoaded={isRunLoaded}
                                readyError={readyError}
                                backstory={crucible.backstory ?? null}
                                invite={crucible.inviteCode && isPartyCrucible(crucible) && !isStarting && !isDirectEntry
                                    ? { code: crucible.inviteCode, link: buildInviteLink(window.location.origin, crucible.inviteCode) }
                                    : null}
                            />
                        </HudAreaSlot>
                    </>
                )}
                {/* The run's party panel, with your Ready toggle and row menus (Leave, the leader's Remove) added; the chat under it. */}
                <HudAreaSlot panel="party">
                    <PartyTabs
                        party={(
                            <PartyPanel
                                rows={partyRows}
                                rowActions={partyRowActions}
                                markerToggle={partyMarkerToggle}
                            />
                        )}
                        chat={partyChat}
                        isPartyAttentionNeeded={isAwaitingReady}
                    />
                </HudAreaSlot>
            </HudAreaFrame>

            {/* The first gathering's tour (#234); the countdown's and Ready's callouts wait for it. */}
            <GuidedTour tourId='gathering' isEnabled={isGatheringReady && !isDirectEntry && !isStarting && crucible !== null && self !== null} />

            {beforeCutscene}
        </div>
    );
}

/** The box the scene's countdown number fills: centered on its spot, about as tall as its digits (gathering-set.ts). */
function resolveCountdownAnchorStyle(entrance: GatheringEntranceSpot): CSSProperties {
    const sizeVh = entrance.holeHeight * 100 * COUNTDOWN_ANCHOR_SHARE;
    return {
        left: `${entrance.anchor.x * 100}%`,
        top: `${entrance.anchor.y * 100}%`,
        width: `${sizeVh}vh`,
        height: `${sizeVh}vh`,
    };
}
